<template>
  <div>
    <v-progress-linear v-if="loading" :indeterminate="true"/>
    <v-dialog v-model="showExportDialog" persistent max-width="1000">
      <!-- before the export starts: what it will run over, and how -->
      <v-card v-if="exportSetup">
        <v-card-title>Export to Onfleet</v-card-title>
        <v-card-text>
          <p>{{ exportSetup.deliveries }} deliveries on this page.</p>
          <v-checkbox v-model="exportCombine" hide-details
                      label="Add customers to the Onfleet task already at their address"/>
          <p class="text-caption mt-2 mb-0">
            When an earlier export stopped or failed, some customers at an address may already be on a task.
            With this on, the others are added to that task (and its name becomes "2 PEOPLE | ..."), so the
            address stays one stop. With it off, those addresses are left as they are.
            An address that is already on more than one task is never changed - it is listed as a warning.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined @click="exportSetup = null; showExportDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="runExportAll">Start export</v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else>
        <v-card-title v-if="exporting">Exporting</v-card-title>
        <v-card-title v-if="!exporting">Exporting Completed</v-card-title>
        <v-card-text>
          <v-progress-linear :value="exportPercent" height="20" color="primary" class="mb-3">
            {{ exportStatus.tasksCompleted || 0 }} of {{ exportStatus.tasksToComplete || 0 }} deliveries
          </v-progress-linear>
          <div>
            {{ exportStatus.exported || 0 }} exported
            &middot; {{ exportStatus.alreadyExported || 0 }} already in Onfleet
            <template v-if="exportStatus.notCombined">
              &middot; {{ exportStatus.notCombined }} partly in Onfleet, not combined
            </template>
            &middot; {{ exportStatus.skipped || 0 }} skipped (pickup, donation or an Onfleet task)
            &middot; <span :class="{'warning--text font-weight-bold': exportStatus.warnings}">{{ exportStatus.warnings || 0 }} warnings</span>
            &middot; <span :class="{'error--text font-weight-bold': exportStatus.failed}">{{ exportStatus.failed || 0 }} failed</span>
          </div>
          <div v-if="exporting && exportStatus.current" class="text-caption mt-1">Now: {{ exportStatus.current }}</div>
          <v-list dense v-if="showErrorsInDialog">
            <v-list-item v-for="(w,index) of (exportStatus.warningList||[])" v-bind:key="'w' + index">
              <v-alert color="warning" outlined class="mb-1">{{ addressLabel(w.address) }} : {{ w.message }}</v-alert>
            </v-list-item>
            <v-list-item v-for="(e,index) of (exportStatus.errors||[])" v-bind:key="index">
              <v-list-item-content>
                <v-list-item-title>
                  <v-alert color="error" outlined>
                    {{ e.onfleetPayload && addressLabel(e.onfleetPayload.address) }} :
                    {{ e.errorMessage }}
                  </v-alert>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatOnfleetPayload(e.onfleetPayload) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="exportProblemCount" outlined @click="showErrorsInDialog=!showErrorsInDialog">
            {{ showErrorsInDialog ? 'Hide' : 'Show' }} warnings and errors ({{ exportProblemCount }})
          </v-btn>
          <v-spacer/>
          <template v-if="exporting && pageJob">
            <v-btn outlined color="error" class="mr-2" :disabled="pageJob.stopRequested" @click="stopPageJob">
              {{ pageJob.stopRequested ? 'Stopping' : 'Stop' }}
            </v-btn>
            <v-btn outlined @click="hideJobDialog" title="the export keeps running on the server">Hide</v-btn>
          </template>
          <v-btn v-else outlined :loading="exporting" @click="exportStatus={}; showExportDialog=false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="showNotices" :timeout="-1" color="warning" multi-line>
      <div v-for="(message,key) of notices" v-bind:key="key">{{ message }}</div>
      <template v-slot:action="{attrs}">
        <v-btn text v-bind="attrs" @click="clearNotices">Close</v-btn>
      </template>
    </v-snackbar>
    <v-dialog v-model="showCheckDialog" persistent max-width="680px">
      <v-card>
        <v-card-title>{{ checkFinished ? 'Check finished' : 'Check Onfleet Status' }}</v-card-title>
        <v-card-text v-if="!checking && !checkFinished">
          <p>
            Pick what to run over {{ checkOrders ? 'this stop' : 'this shipment' }}. None of these create or change
            anything in Onfleet.
          </p>
          <v-btn block outlined color="green" @click="runOnfleetCheck('status')">
            Check and update the export status
          </v-btn>
          <p class="mt-2 mb-6 grey--text text--darken-1">
            Does each order's task still exist in Onfleet, and does the task still list the order? An order whose
            task has been deleted has its reference removed here, so it can be re-exported. An order whose task
            was since linked to somebody else is reported, so it can be linked again. Where the task does list
            the order, the driver saved here is set to the driver the task is assigned to in Onfleet. Nothing is
            posted to Slack.
          </p>
          <v-btn block outlined color="green" @click="runOnfleetCheck('comparison')">
            Post a comparison of every stop to Slack
          </v-btn>
          <p class="mt-2 mb-1 grey--text text--darken-1">
            One message per stop: what the export would send now, next to what Onfleet holds - delivery window,
            address, driver, eta - plus the task json.
          </p>
          <v-checkbox v-model="checkOnlyProblems" hide-details dense class="mt-0 mb-6"
                      label="Only post stops with a warning or an error"/>
          <v-btn block outlined color="green" @click="runOnfleetCheck('concise')">
            Post a concise problem report to Slack
          </v-btn>
          <p class="mt-2 mb-0 grey--text text--darken-1">
            One message for the whole {{ checkOrders ? 'stop' : 'shipment' }}, one row per customer: who is not
            getting the window they were promised, and who has a task whose window disagrees with ours.
          </p>
          <v-alert v-if="checkStopCount > 50" class="mt-4 mb-0" color="warning" outlined dense>
            This takes a while: {{ checkStopCount }} stops, and the comparison posts to Slack about one per second.
          </v-alert>
        </v-card-text>
        <v-card-text v-else>
          <v-progress-linear :value="checkPercent" height="20" class="mb-4" color="green">
            {{ checkPercent }}%
          </v-progress-linear>
          <div v-if="checkProgress.ordersToCheck">
            Deliveries checked: {{ checkProgress.ordersChecked }} of {{ checkProgress.ordersToCheck }}
          </div>
          <div v-if="checkProgress.stopsToCheck">
            Stops compared with Onfleet: {{ checkProgress.stopsChecked }} of {{ checkProgress.stopsToCheck }}
          </div>
          <v-list dense v-if="checkFinished">
            <v-list-item v-for="(message,index) of checkProgress.messages" v-bind:key="index">
              {{ message }}
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn v-if="checking && pageJob" outlined class="mr-2" @click="hideJobDialog"
                 title="the check keeps running on the server">Hide</v-btn>
          <v-btn outlined :disabled="checkCancelled" @click="closeOnfleetCheck">{{ checkButtonLabel }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="filterPrompt.show" persistent max-width="560px">
      <v-card>
        <v-card-title>Filters are on</v-card-title>
        <v-card-text>
          <p>
            This page is filtered by
            <v-chip v-for="filter of activeFilters" v-bind:key="filter" small class="mr-1 mb-1" label>{{ filter }}</v-chip>
          </p>
          <p class="mb-0">
            <b>{{ filterPromptAction }} everything</b> ignores the filters, so every delivery on this page is
            {{ filterPrompt.verb }}. That is what you want nearly every time.
          </p>
          <v-alert type="warning" outlined dense class="mt-4 mb-0">
            <b>Keep the filters</b> only runs over what the filters show. Customers at the same address that the
            filters split up end up as separate Onfleet stops.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="answerFilterPrompt(null)">Cancel</v-btn>
          <v-spacer/>
          <v-btn outlined color="warning" class="mr-2" @click="answerFilterPrompt('filtered')">Keep the filters</v-btn>
          <v-btn color="green" dark depressed @click="answerFilterPrompt('everything')">
            {{ filterPromptAction }} everything
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-container class="orders" fluid v-if="!loading">
      <v-footer fixed class="d-print-none pl-10 pr-8" v-if="datesFormatted">
        <!-- each chip toggles the filter that shows just those deliveries; a filled chip with an x is on -->
        <v-chip v-for="chip of footerChips" v-bind:key="chip.label" small class="mr-2"
                :color="chip.color"
                :outlined="!chip.active"
                :close="chip.active"
                @click="chip.toggle"
                @click:close="chip.toggle">
          {{ chip.count }} {{ chip.label }}
        </v-chip>
        <template v-if="activeFilters.length">
          <span class="text-caption font-weight-bold ml-2">Filtered: {{ activeFilters.join(', ') }}</span>
          <v-btn x-small outlined class="ml-2" @click="resetFilters">Reset filters</v-btn>
        </template>
        <v-spacer/>
        {{ footerTotals.deliveries }} deliveries &middot; {{ footerTotals.boxes }} boxes
      </v-footer>
      <v-alert color="red" v-if="isAdmin">Danger Danger! Admin mode</v-alert>
      <v-toolbar flat class="d-print-none pb-10">
        <ShippingGroupSelector
          :dates.sync="dates"
          :shippingGroup.sync="shippingGroup"
          v-on:update="fetchData"
        />
        <!--        <v-text-field :value="datesFormatted"-->
        <!--                      label="Select dates"-->
        <!--                      single-line-->
        <!--                      hide-details-->
        <!--                      readonly-->
        <!--                      @click="showDatePicker=true"-->
        <!--                      append-icon="mdi-calendar"-->
        <!--        />-->
        <!--        <v-spacer/>-->
        <!--        <v-text-field-->
        <!--          label="Search"-->
        <!--          v-model="search"-->
        <!--          single-line-->
        <!--          hide-details-->
        <!--          clearable-->
        <!--          append-icon="mdi-magnify"-->
        <!--        />-->
        <v-spacer/>
        <v-select
          label="Filter by driver"
          v-model="driverSelected"
          :items="drivers"
          single-line
          hide-details
          clearable
          style="width: 20px"
        />
        <v-select
          class="pl-2"
          label="Location"
          flat
          hide-details
          v-model="filterLocation"
          :items="['','home','office']"
          style="width: 20px"
        />
        <v-select
          class="pl-2"
          label="Delivery City"
          flat
          hide-details
          v-model="filterDistributionCity"
          :items="distributionCities"
          style="width: 20px"
          clearable
        />
        <v-checkbox
          class="pl-2 pt-2"
          label="UID"
          v-model="showUid"
          hide-details
        />
        <v-dialog
          v-model="showDatePicker"
          ref="dialog"
          width="290px"
          persistent
        >
          <v-date-picker
            v-model="datePickerDate"
            :range="isAdmin"
            no-title
            @close="!isAdmin && closeDatePicker()"
            @change="closeDatePicker"
          ></v-date-picker>
          <v-btn v-if="isAdmin" @click="closeDatePicker">Close</v-btn>
        </v-dialog>
      </v-toolbar>
      <template v-if="!exporting">
        <template v-if="!readOnly">
          <v-dialog v-model="showLinkDialog" max-width="600px">
            <v-card>
              <v-card-title>Link Task</v-card-title>
              <v-card-text>
                <p>
                  This will create a link between an onfleet task (created in onfleet) and a FED delivery, so that ETA,
                  driver assignment, SMS, etc, are
                  all work correctly. You should not need to use this; the export button on this page should be enough.
                </p>
                <p>Address to link: <b>{{ linkOrders && linkOrders.address }}</b></p>
                <v-select
                  label="Onfleet Task"
                  v-model="onfleetShortId"
                  :items="onfleetTasks"
                  :item-value="t => t.shortId"
                  :item-text="t => t.customer ? `${t.shortId} - ${formatAddress(t.customer.address)}` : JSON.stringify(t)"
                  clearable
                  class="mr-2"
                  hint="Something like this: d588f4d4"
                />
                <p><b>Important:</b> for an onfleet task to appear in the dropdown above it must be assigned to a
                  driver on the FED team, have an ETA on one of the dates shown on this page, and not already be
                  linked to a FED order.</p>
                <v-text-field
                  label="Or enter an onfleet task id manually"
                  v-model="manualOnfleetTaskId"
                  clearable
                  class="mr-2"
                  hint="Either the 8 character shortId (e.g. d588f4d4) or the long task id from the onfleet URL"
                  persistent-hint
                />
                <p> Selected onfleet task: {{ linkTaskId || '(none)' }} </p>
                <p> Fed order references: {{ linkOrderRefs.join(', ') }}</p>
                <v-alert v-if="linkProblems.length" type="warning" outlined dense>
                  <p class="mb-1"><b>This task does not look like the right one:</b></p>
                  <ul class="mb-2">
                    <li v-for="problem of linkProblems" :key="problem">{{ problem }}</li>
                  </ul>
                  <p class="mb-1" v-if="linkTaskSummary">
                    Onfleet task <b>{{ linkTaskSummary.shortId }}</b>:
                    {{ linkTaskSummary.address || '(no address)' }},
                    driver {{ linkTaskSummary.driver || '(unassigned)' }}
                  </p>
                  <p class="mb-0">
                    Check the task in onfleet. If you are sure it is right, press <b>Link anyway</b>;
                    this will be reported in slack.
                  </p>
                </v-alert>
                <!--              <pre>{{linkOrders && linkOrders.addressOrders}}</pre>-->
              </v-card-text>
              <v-card-actions>
                <v-btn @click="closeLinkDialog">Cancel</v-btn>
                <v-spacer/>
                <v-btn v-if="!linkProblems.length" :disabled="!linkTaskId || !linkOrderRefs.length"
                       :loading="linking"
                       @click="linkTask(linkOrderRefs,linkTaskId)">Link</v-btn>
                <v-btn v-else color="error" outlined :loading="linking"
                       @click="linkTask(linkOrderRefs,linkTaskId,{force:true})">Link anyway</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-toolbar flat class="d-print-none">
            <v-btn outlined class="ml-2" :loading="exporting" @click="startExportAll"
                   :disabled="shippingGroup!=='local'">
              Export to Onfleet
            </v-btn>
            <v-checkbox label="Show Errors" class="ml-2" v-model="onlyErrors" hide-details/>
            <v-checkbox label="Show Not Exported" class="ml-2" v-model="notExported" hide-details/>
            <v-btn v-if="isAdmin" x-small color="error" outlined class="ml-2" :loading="exporting"
                   @click="resetOnfleetStatus">
              Reset Onfleet Status
            </v-btn>
            <v-btn color="green" outlined class="ml-2" :loading="exporting" @click="openOnfleetCheck()"
                   :disabled="shippingGroup!=='local'">
              Check Onfleet Status
            </v-btn>
            <v-btn outlined class="ml-2" @click="downloadDebugDump">Debug dump</v-btn>
            <v-spacer/>
            <v-btn outlined @click="showTable=!showTable">{{ showTable ? 'Hide' : 'Show' }} CSV</v-btn>
            <v-btn outlined class="ml-2" @click="exportAddressCSV">Download CSV</v-btn>
            <v-btn :loading="generatingPDF" outlined class="ml-2" @click="downloadPDF($event.target)">Download PDF
            </v-btn>
          </v-toolbar>
        </template>
        <v-data-table
          v-if="showTable"
          :headers="dataHeaders"
          :items="dataAsTable"
          disable-pagination
          hide-default-footer
          dense
        />
        <v-toolbar flat dense class="d-print-none" v-if="groupBy==='deliveries' && !showTable">
          <v-btn
            small
            color="warning"
            :outlined="etaStatusFilter !== 'warning'"
            @click="etaStatusFilter = etaStatusFilter === 'warning' ? null : 'warning'"
          >
            {{ etaStatusCounts.warning }} ETA warnings
          </v-btn>
          <v-btn
            small
            color="error"
            class="ml-2"
            :outlined="etaStatusFilter !== 'error'"
            @click="etaStatusFilter = etaStatusFilter === 'error' ? null : 'error'"
          >
            {{ etaStatusCounts.error }} ETA errors
          </v-btn>
          <v-btn
            small
            color="success"
            class="ml-2"
            :outlined="etaStatusFilter !== 'ok'"
            @click="etaStatusFilter = etaStatusFilter === 'ok' ? null : 'ok'"
          >
            {{ etaStatusCounts.ok }} on time
          </v-btn>
          <span class="text-caption grey--text text--darken-1 ml-4">
            The ETA from Onfleet (or the actual delivery time, once delivered) compared with the delivery window.
            <br/>
            <b>Warning:</b> outside the window - default delivery times being ignored.
            <br/>
            <b>Error:</b> outside the window - customer provided delivery times ignored!
            <br/>
            <b>On time:</b> inside the delivery window.
          </span>
        </v-toolbar>
        <template v-if="groupBy==='deliveries' && !showTable">
          <!-- every card is built once per data load; the filters only hide and show them -->
          <v-card
            style="page-break-after: always;"
            flat
            v-for="section of driverSections"
            v-bind:key="section.driver"
            v-show="section.shown"
          >
            <v-card-title>
              Driver: {{ section.driver }}
            </v-card-title>
            <ShipmentStopCard
              v-for="{stop, shown} of section.stops"
              v-bind:key="stop.key"
              v-show="shown"
              :stop="stop"
              :exporting="exporting"
              :shipping-group="shippingGroup"
              @export="exportToOnfleet2({address: $event.address, orders: $event.orders})"
              @combine="combineStop"
              @check="openOnfleetCheck($event.orders)"
              @link="showLinkDialog = true; linkOrders = {address: $event.address, addressOrders: $event.orders}"
              :read-only="readOnly"
              @coordinates="setStopCoordinates"
            />
          </v-card>
        </template>
        <!-- blank gap at the bottom to avoid fixed footer hiding data-table -->
      </template>
      <div class="ma-8"></div>
    </v-container>
  </div>
</template>


<script>
import {mapActions, mapGetters} from 'vuex'
import moment from 'moment';
import {
  addressGroupKey,
  csvExport,
  dateFormatted,
  escapeRegex,
  formatAddress,
  getAddress,
  getDateString,
  getDeliveryInstruction,
  getEarliestDeliveryTime,
  getLatestDeliveryTime,
  parseTimeToMinutes,
  getProductionDays,
  groupOrdersByDelivery,
  sortOrderGroups,
} from '@/store/utils'
import api from '@/api';
import {buildPickupLookup, isPickupAddress} from 'fed-lib/src/pickup';
import BagCalc from "@/store/bag-calc";
import bagCalc from "@/store/bag-calc";
import {groupBy as groupByKey, range} from "lodash";
import urlState from "@/router/urlState";
import ShippingGroupSelector from "@/components/ShippingGroupSelector.vue";
import ShipmentStopCard from "@/components/ShipmentStopCard.vue";
import diff from "microdiff";
import {coordinatesState, onfleetParsedAddress} from "@/utils/address-coordinates";


export default {
  components: {ShippingGroupSelector, ShipmentStopCard},
  mixins: [urlState],
  data() {
    return {
      units: ['g', 'kg'],
      decimalPlaces: [1, 2],
      streamsSelected: [],
      driverSelected: null,
      selected: [],
      missing_meals: new Set(),
      missing_count: {},
      showDatePicker: false,
      dates: [],
      search: '',
      loading: false,
      post: null,
      error: null,
      multiview: false,
      multiviewMealSelected: {},
      headers: [
        // {
        //   text: 'Email',
        //   align: 'start',
        //   value: 'email',
        //   width: '*'
        // },
        {text: 'Driver', value: 'driver'},
        {text: 'Customer Name', value: 'name',},
        {text: 'Quantity', value: 'quantity'},
        // {text: 'Meal ID', value: 'meal_id'},
        {text: 'Meal', value: 'mealName'},
        {text: 'Stream', value: 'stream'},
        {text: 'Allergy/Restrictions', value: 'displayAllergies',},

        {text: 'Time of day', value: 'tod'},
        {text: 'Date', value: 'date'}

      ],
      showMissing: false,
      filteredIngredients: [],
      maxEmailPerLink: 450,
      groupBy: 'deliveries',
      showAssignInProgress: null,
      showClearInProgress: null,
      datePickerDate: null,
      filterLocation: null,
      filterDistributionCity: null,
      exporting: null,
      // toast messages by key, kept until dismissed - an alert() would pause the export
      notices: {},
      showNotices: false,
      donationsRemoved: 0,
      pickupsSkipped: 0,
      pickupLookup: new Set(),
      rackConfigs: [],
      overrideShipDate: '',
      onlyErrors: false,
      // show only deliveries whose eta is outside the window: 'warning' | 'error' | null
      etaStatusFilter: null,
      // show only deliveries on an onfleet task, or not on one: 'with' | 'without' | null
      taskFilter: null,
      // show only addresses with more than one customer
      sharedOnly: false,
      showUid: false,
      showExportDialog: null,
      exportStatus: {errors: []},
      // set while the export dialog asks how to run the export, before it starts
      exportSetup: null,
      // the export or check running on the api for this page (see watchJob), and its poll timer
      pageJob: null,
      jobPoll: null,
      exportCombine: false,
      // rows collected during an export, posted to slack as one concise report
      exportReport: null,
      showCheckDialog: false,
      // the "filters are on" prompt before a whole-page export or check: verb is what the run
      // does to a delivery ('checked' | 'exported'), resolve answers confirmFilters' promise
      filterPrompt: {show: false, verb: '', resolve: null},
      // the stop being checked, or null for the whole shipment
      checkOrders: null,
      checkIgnoresFilters: false,
      // which check is running or just ran: 'status' | 'comparison' | 'concise'
      checkMode: null,
      checkOnlyProblems: true,
      checking: false,
      checkCancelled: false,
      checkFinished: false,
      checkProgress: {ordersChecked: 0, ordersToCheck: 0, stopsChecked: 0, stopsToCheck: 0, messages: []},
      onfleetShortId: '',
      manualOnfleetTaskId: '',
      linking: false,
      linkProblems: [],
      linkTaskSummary: null,
      showLinkDialog: false,
      driverTasks: [],
      generatingPDF: false,
      showErrorsInDialog: null,
      notExported: null,
      linkOrders: null,
      shippingGroup: null,
      currentData: null,
      ordersFetched: [],
      customersRaw: null,
      showTable: null
    }
  },
  beforeDestroy() {
    this.stopWatchingJob();
  },
  // kept alive while another page shows: stop polling, and look for a running job on return
  deactivated() {
    this.stopWatchingJob();
  },
  activated() {
    this.resumeRunningJob();
  },
  mounted() {
    this.syncToUrl({
      param: 'filterLocation', urlParam: 'location', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'filterDistributionCity', urlParam: 'city', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'search', urlParam: 'search', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'shippingGroup', urlParam: 'shippinggroup', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'driverSelected', urlParam: 'driver', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'groupBy', urlParam: 'group', initFromRoute: true,
      // parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'onlyErrors', urlParam: 'errors', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });
    this.syncToUrl({
      param: 'etaStatusFilter', urlParam: 'etaStatus', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'taskFilter', urlParam: 'tasks', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'sharedOnly', urlParam: 'shared', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    });

    this.setDates();
    this.fetchData();

    api.get('v2/shipping/pickup-locations')
      .then(({data}) => {
        this.pickupLookup = buildPickupLookup(data);
      })
      .catch(e => console.error('failed to load pickup locations', e));

    api.get('v2/order/rack-configs')
      .then(r => this.rackConfigs = r.data)
      .catch(e => console.error('failed to load rack configs', e));
  },
  watch: {
    /** a refusal applies to one task; picking a different one starts the checks over */
    linkTaskId() {
      this.linkProblems = [];
      this.linkTaskSummary = null;
    },
    showDatePicker(v) {
      if (v) {
        if (this.isAdmin) {
          this.datePickerDate = this.dates;
        } else {
          this.datePickerDate = this.dates[0];
        }
      } else {
        if (this.isAdmin) {
          this.dates = this.datePickerDate;
        } else {
          this.dates = getProductionDays(this.datePickerDate)
        }
      }
    },
  },
  computed: {
    ...mapGetters([
      'meals',
      'diets',
      'getMeal',
      'getMealIngredients',
      'getDietName',
      'getDietStream',
      'getTimeOfDay',
      'getSummaryForDiet',
    ]),
    // showWarning(){
    //   if (this.da)
    // },
    readOnly() {
      return !!this.$route.meta.readOnly;
    },
    /** stops in the selection the check dialog is about */
    checkStopCount() {
      if (!this.showCheckDialog) {
        return 0;
      }
      const orders = this.checkOrders || this.checkPageOrders;
      return orders ? Object.keys(this.groupOrdersBy(orders, 'address')).length : 0;
    },
    /** what a whole-page check runs over: the filtered list only when the user chose that */
    checkPageOrders() {
      return this.checkIgnoresFilters ? this.ordersWithoutFilters : this.orders;
    },
    checkButtonLabel() {
      if (this.checkFinished) return 'Close';
      if (this.checkCancelled) return 'Stopping';
      return this.checking ? 'Stop' : 'Cancel';
    },
    /** one job (export or check) runs on the api per page: this shipping group and these dates */
    pageJobKey() {
      return `${this.shippingGroup}|${[...(this.dates || [])].sort().join(',')}`;
    },
    exportProblemCount() {
      return (this.exportStatus.errors || []).length + (this.exportStatus.warningList || []).length;
    },
    exportPercent() {
      const {tasksCompleted, tasksToComplete} = this.exportStatus;
      return tasksToComplete ? Math.round((tasksCompleted / tasksToComplete) * 100) : 0;
    },
    checkPercent() {
      const {ordersChecked, ordersToCheck, stopsChecked, stopsToCheck} = this.checkProgress;
      const total = ordersToCheck + stopsToCheck;
      return total ? Math.round(((ordersChecked + stopsChecked) / total) * 100) : 0;
    },
    linkTaskId() {
      const manual = (this.manualOnfleetTaskId || '').trim();
      return manual || this.onfleetShortId || '';
    },
    /** the FED side of the link, so the server can check the onfleet task is for the same delivery */
    linkDelivery() {
      const orders = this.linkOrders
        ? this.linkOrders.addressOrders.filter(o => !!o.lineItemRef)
        : [];
      if (!orders.length) {
        return {};
      }
      const first = orders[0];
      const customer = this.getCustomer2(first.uid) || first.customer;
      const address = getAddress(customer, first.date) || first.address || {};
      const names = [...new Set(orders
        .map(o => this.getCustomer2(o.uid) || o.customer)
        .filter(c => !!c)
        .map(c => [c.first_name, c.last_name].filter(Boolean).join(' ')))];
      return {
        address,
        unit: address.unit,
        shipDate: moment(first.ship_date).format(moment.HTML5_FMT.DATE),
        name: names.join(', ')
      };
    },
    linkOrderRefs() {
      const orderRefs = this.linkOrders
        ? [...new Set(
          this.linkOrders.addressOrders
            .filter(o => !!o.lineItemRef)
            .map(o => `${o.order_id}/logistics/${moment(o.ship_date).format('ddd').toLowerCase()}`))]
        : [];
      return orderRefs;
    },
    isFiltered() {
      const allOrders = this.ordersUnfiltered.length;
      const displayedOrders = this.ordersFiltered.length;
      console.log('is fitlered', {allOrders, displayedOrders});
      return allOrders !== displayedOrders;
    },
    ordersByAddress() {
      // console.log('orders', this.orders);
      const result = {};
      this.orders.forEach(o => {
        const {customer} = o;
        const address = getAddress(o.customer, o.date);
        const display = [customer.first_name, customer.last_name, address.unit || '', address.line1, address.postal_code].join(' ');
        result[o.uid] = display
      })
      return Object.entries(result)
        .map(([key, value]) => ({key, value}));
    },


    ordersUnfiltered() {
      const orders = this.ordersFetched;
      // console.log('concat orders from dates', {dates: this.dates});

      // orders.forEach(o => o.name = `${o.last_name}, ${o.first_name}`);
      orders.forEach(o => o.name = `${o.first_name} ${o.last_name}`);
      // orders.forEach(o => o.mealName = this.getMeal(o.meal_id) && this.getMeal(o.meal_id).name);
      orders.forEach(o => o.customer = this.getCustomer2(o.uid));
      orders.forEach(o => o.email = o.customer ? o.customer.email : '');
      orders.forEach(o => o.driver = o.driver || 'Unassigned');

      const ship_dates = [...new Set(orders.map(o => o.ship_date))];
      // console.log('ship_dates', ship_dates);
      this.driverTasks.forEach(dt => {
        const task = dt.task;
        // const worker = dt.worker;
        const ship_date = moment(task.eta).format(moment.HTML5_FMT.DATE);
        // console.log('checking task ship date',ship_date,dt)
        if (!ship_dates.includes(ship_date)) {
          // console.log('ship date excluded',ship_date, task.eta, worker.name)
        } else {
          const taskAddress = task.address;
          const address = {
            unit: taskAddress.apartment,
            state: 'British Columbia',
            postal_code: taskAddress.postalCode, //: 'V6A 2M9',
            line1: `${taskAddress.number} ${taskAddress.street}`,
            city: taskAddress.city, // : 'Vancouver',
            country: taskAddress.country, //: 'Canada'
            delivery_instruction: task.notes
          }
          const fauxOrder = {
            isOnfleetTask: true,
            shortId: task.shortId,
            eta: task.eta,
            name: dt.task.name + ' (onfleet)',
            driver: dt.worker.name,
            address,
            customer: {
              address,
            },
            quantity: 1
          };
          console.log('adding faux order', fauxOrder);
          orders.push(fauxOrder);
        }
      })

      orders.forEach(o => {
        if (!o.address) {
          console.warn('order is missing address!', o);
          o.address = {};
        }
      })
      return orders;
    },
    onfleetTasks() {
      return this.ordersUnfiltered.filter(o => o.isOnfleetTask);
    },
    /** every order this page delivers, before any of the filters the user can set */
    ordersWithoutFilters() {
      // filter out the 'use_shipping' customers
      return this.ordersUnfiltered.filter(o => {
        const address = getAddress(o.customer, o.date);
        if (!address) {
          console.log('no address', o);
          return true;
        }
        return !address.use_shipping;
      });
    },
    /**
     * the footer's count chips, each toggling the filter that shows just those deliveries;
     * a chip with nothing to show is left out, unless its filter is on (so it can be turned off)
     */
    footerChips() {
      const totals = this.footerTotals;
      return [
        {
          label: 'ETA warnings', count: totals.etaWarnings, color: 'warning',
          active: this.etaStatusFilter === 'warning',
          toggle: () => this.etaStatusFilter = this.etaStatusFilter === 'warning' ? null : 'warning'
        },
        {
          label: 'ETA errors', count: totals.etaErrors, color: 'error',
          active: this.etaStatusFilter === 'error',
          toggle: () => this.etaStatusFilter = this.etaStatusFilter === 'error' ? null : 'error'
        },
        {
          label: 'unassigned', count: totals.unassigned, color: 'warning',
          active: this.driverSelected === 'Unassigned',
          toggle: () => this.driverSelected = this.driverSelected === 'Unassigned' ? null : 'Unassigned'
        },
        {
          label: 'export errors', count: totals.exportErrors, color: 'error',
          active: !!this.onlyErrors,
          toggle: () => this.onlyErrors = !this.onlyErrors
        },
        {
          label: 'with tasks', count: totals.withTasks, color: 'primary',
          active: this.taskFilter === 'with',
          toggle: () => this.taskFilter = this.taskFilter === 'with' ? null : 'with'
        },
        {
          label: 'without tasks', count: totals.withoutTasks, color: 'primary',
          active: this.taskFilter === 'without',
          toggle: () => this.taskFilter = this.taskFilter === 'without' ? null : 'without'
        },
        {
          label: 'more than one customer', count: totals.shared, color: 'primary',
          active: this.sharedOnly,
          toggle: () => this.sharedOnly = !this.sharedOnly
        }
      ].filter(chip => chip.count || chip.active);
    },
    /** the filters set on the page, described for the warning before an export or check */
    /** the verb for the filter prompt's buttons: Check or Export */
    filterPromptAction() {
      return this.filterPrompt.verb === 'exported' ? 'Export' : 'Check';
    },
    activeFilters() {
      return [
        this.driverSelected && `driver ${this.driverSelected}`,
        this.onlyErrors && 'Show Errors',
        this.notExported && 'Show Not Exported',
        this.search && `search "${this.search}"`,
        this.filterLocation && `location ${this.filterLocation}`,
        this.filterDistributionCity && this.filterDistributionCity.length
        && `distribution city ${[].concat(this.filterDistributionCity).join(', ')}`,
        this.etaStatusFilter && `ETA ${this.etaStatusFilter}`,
        this.taskFilter && `${this.taskFilter} tasks`,
        this.sharedOnly && 'more than one customer'
      ].filter(f => !!f);
    },
    ordersBeforeEtaFilter() {
      let orders = this.ordersWithoutFilters;

      // console.log('orders', orders);
      // const streamsSelected = this.streamsSelected;
      if (this.driverSelected) {
        orders = orders.filter(o => o.driver === this.driverSelected);
      }

      if (this.onlyErrors) {
        orders = orders.filter(o => {
          const [deliveryStatus = {}] = this.getDeliveryStatus([o]);
          return !!deliveryStatus.error;
        });
      }

      if (this.notExported) {
        orders = orders.filter(o => !this.isExported([o]));
      }


      if (this.search) {
        const regex = new RegExp(escapeRegex(this.search), 'i');
        orders = orders.filter(o => [o.name, formatAddress(getAddress(o.customer, o.date))].join('').match(regex))
      }

      if (this.filterLocation) {
        orders = orders.filter(o => getAddress(o.customer, o.date).location === this.filterLocation);
      }

      if (this.filterDistributionCity) {
        orders = orders.filter(o => this.filterDistributionCity.includes(o.distributionCity));
      }
      return orders;
    },
    ordersFiltered() {
      let orders = this.ordersBeforeEtaFilter;
      if (this.etaStatusFilter) {
        // keeps whole deliveries: every order in a card whose eta has this status
        const stopByOrder = this.stopByOrder;
        const status = this.etaStatusFilter;
        orders = orders.filter(o => {
          const stop = stopByOrder.get(o);
          return stop && stop.etaStatus === status;
        });
      }
      if (this.taskFilter || this.sharedOnly) {
        // whole deliveries again: by what the card has, not each order
        const stopByOrder = this.stopByOrder;
        orders = orders.filter(o => {
          const stop = stopByOrder.get(o);
          if (!stop) {
            return false;
          }
          if (this.taskFilter && (stop.tasks.length > 0) !== (this.taskFilter === 'with')) {
            return false;
          }
          return !this.sharedOnly || stop.customerCount > 1;
        });
      }
      return orders;
    },
    /** deliveries (cards) by eta status, before the eta filter itself */
    etaStatusCounts() {
      const counts = {ok: 0, warning: 0, error: 0};
      for (const stop of this.stopsShownBy(this.ordersBeforeEtaFilter)) {
        if (stop.etaStatus) {
          counts[stop.etaStatus]++;
        }
      }
      return counts;
    },
    orders() {
      // used to do fruit cup hack here
      return this.ordersFiltered;
    },
    /**
     * the footer counts, over the deliveries in the list (one per address, as the cards
     * show them): boxes are qr codes, the rest count deliveries
     */
    footerTotals() {
      // onfleet tasks not linked to any order are not deliveries of ours
      const deliveries = this.shownStops.filter(stop => stop.customerCount > 0);
      const totals = {
        deliveries: deliveries.length, withTasks: 0, withoutTasks: 0, shared: 0,
        boxes: 0, etaWarnings: 0, etaErrors: 0, unassigned: 0, exportErrors: 0
      };
      for (const stop of deliveries) {
        totals.boxes += stop.boxes;
        if (stop.tasks.length) totals.withTasks++;
        else totals.withoutTasks++;
        if (stop.customerCount > 1) totals.shared++;
        if (stop.etaStatus === 'warning') totals.etaWarnings++;
        if (stop.etaStatus === 'error') totals.etaErrors++;
        if (stop.driver === 'Unassigned') totals.unassigned++;
        if (stop.customers.some(c => c.errors.length)) totals.exportErrors++;
      }
      return totals;
    },
    mealsByDiet() {
      console.log('this.getMealsByDiet(this.date)', this.getMealsByDiet(this.date));
      return this.getMealsByDiet(this.date);
    },
    summary() {
      return this.$store.getters.summary(this.date);
    },
    /** the deliveries list, per driver: one card per address */
    /**
     * every delivery card, per driver, built once per data load from all the orders (not
     * the filtered ones) with what the card shows worked out up front. the filters only
     * hide and show cards (driverSections): rebuilding ~700 cards on every filter change
     * took seconds. a card always holds everyone at its address, so an export from it
     * never leaves anyone out.
     */
    deliveryStopsByDriver() {
      const ordersByDriver = groupByKey(this.ordersWithoutFilters, o => o.driver);
      const stopsByDriver = {};
      for (const driver of this.drivers) {
        stopsByDriver[driver] = this.groupOrdersByAddressWithTasks(ordersByDriver[driver] || [])
          .map(stop => ({...stop, key: `${driver}|${stop.address}`}));
      }
      return stopsByDriver;
    },
    deliveryStops() {
      return Object.values(this.deliveryStopsByDriver).flat();
    },
    stopByOrder() {
      const stopByOrder = new Map();
      this.deliveryStops.forEach(stop => stop.orders.forEach(o => stopByOrder.set(o, stop)));
      return stopByOrder;
    },
    /** the cards the filters show: those with at least one order the filters keep */
    shownStops() {
      return this.stopsShownBy(this.orders);
    },
    /**
     * what the list renders: every card per driver, with whether the filters show it.
     * the template reads only this, once per render - reading the big computeds above
     * per card made vue walk their (thousands of) dependencies for every card
     */
    driverSections() {
      const shownKeys = new Set(this.shownStops.map(stop => stop.key));
      const stopsByDriver = this.deliveryStopsByDriver;
      return this.drivers.map(driver => {
        const stops = (stopsByDriver[driver] || []).map(stop => ({stop, shown: shownKeys.has(stop.key)}));
        return {driver, stops, shown: stops.some(s => s.shown)};
      });
    },
    /**
     * how many onfleet stops each address (per ship date) is: one per task, plus one
     * when some of its orders are not exported. counted over every order, not only the
     * filtered ones, since the halves of a split address can have different drivers.
     */
    stopsPerAddress() {
      const addresses = {};
      for (const o of this.ordersUnfiltered.filter(o => !o.isOnfleetTask)) {
        const key = this.stopAddressKey(o);
        addresses[key] = addresses[key] || new Set();
        addresses[key].add(this.getOnfleetTaskId(o) || 'not exported');
      }
      return Object.fromEntries(Object.entries(addresses).map(([key, stops]) => [key, stops.size]));
    },
    drivers() {
      const drivers = new Set();
      this.ordersUnfiltered.forEach(o => drivers.add(o.driver));
      console.log('this.driverTasks', this.driverTasks);
      this.driverTasks.forEach(dt => drivers.add(dt.worker.name));
      const sortedDrivers = [...drivers.values()].filter(d => !!d).sort();
      console.log('drivers', sortedDrivers);
      return sortedDrivers;
    },
    datesFormatted() {
      const format = 'dddd MMMM D';
      if (!this.dateFrom) {
        return '';
      } else if (this.dateFrom === this.dateTo) {
        return `${moment(this.dateFrom).format(format)}`;
      } else {
        return `${moment(this.dateFrom).format(format)} - ${moment(this.dateTo).format(format)}`
      }
    },
    dateFrom() {
      return [...this.dates].sort()[0];
    },
    dateTo() {
      return [...this.dates].sort().reverse()[0];
    },
    shipments() {
      let {orders} = this;
      orders = orders.filter(o => getAddress(o.customer, o.date).use_shipping);
      return this.groupOrdersBy(orders, 'uid');
    },
    bags() {
      let {orders, driverSelected, drivers} = this;
      if (driverSelected) {
        orders = orders.filter(o => o.driver === driverSelected);
        drivers = [driverSelected];
      } else {
        drivers = ['Unassigned', ...drivers.filter(d => d !== 'Unassigned')]
      }
      const result = [];
      for (const driver of drivers) {
        const {driverCounts, customerLabels} = BagCalc.computeBags(
          this.groupOrdersBy(orders.filter(o => o.driver === driver)),
          this.rackConfigs
        );
        result.push({
          isDriverLabel: true,
          driver: driver,
          count: driverCounts[driver]
        })
        result.push(...customerLabels.filter(c => c.driver === driver));
      }
      return result;
    },
    selectedUids() {
      return [...new Set(this.ordersFiltered.map(o => o.uid))];
    },
    filename() {
      return `driver-sheets-${this.dates.join()}-${this.driverSelected ? this.driverSelected : 'all'}.pdf`;
    },
    exportFilename() {
      return (`delivery-${this.dates.join('-')}-${this.shippingGroup}` +
        (this.filterDistributionCity ? `-${this.filterDistributionCity}` : '')).toUpperCase();
    },
    dataHeaders() {
      function format(text) {
        const result = text.replace(/([A-Z])/g, " $1");
        return result.charAt(0).toUpperCase() + result.slice(1);
      }

      return Object.keys(this.dataAsTable[0] || {})
        .map(k => ({text: format(k), value: k}));
    },
    dataAsTable() {
      const orders = this.orders;
      const rows = [];
      const headers = 'Order Number\tShipping Date\tClient Name\tTel\tEmail\tAddress\tDepartFrom - Do Not Fill\tGoods\tQty\tNotes/Delivery Details (hour of operation, etc)\tCost ($)\tShipper'.split('\t');
      const ordersByCustomer = this.groupOrdersBy(orders, 'name');
      for (const driver of this.drivers) {
        const orders = this.groupOrdersBy(this.orders.filter(o => o.driver === driver), 'address');
        Object.entries(orders).forEach(([address, orders]) => {
          Object.entries(this.groupOrdersBy(orders, 'name')).forEach(([customerName, customerOrders]) => {
            const date = customerOrders[0].date;
            const ship_date = customerOrders[0].ship_date;
            const customer = customerOrders[0].customer;
            const {phone_number, email} = customer;
            const earliest = this.getEarliestTime(customerOrders, false) || '';
            const latest = this.getLatestTime(customerOrders, false) || '';
            let deliveryTimes = '';
            if (earliest && latest) {
              deliveryTimes = `(${earliest} - ${latest})`;
            } else if (earliest) {
              deliveryTimes = `(after ${earliest})`;
            } else if (latest) {
              deliveryTimes = `(before ${latest})`;
            }

            const instruction = `${this.getDeliveryInstruction(customer, date)} ` + deliveryTimes;


            const row = Object.fromEntries(headers.map(h => [h, '']));

            // {customerName, address, instruction, ship_date, phone, email})
            row['Client Name'] = customerName;
            row['Tel'] = phone_number;
            row['Email'] = email;
            row['Notes/Delivery Details (hour of operation, etc)'] = instruction;
            row['Address'] = address;
            row['Shipping Date'] = ship_date
            // must agree with the Barcodes column: one per box sticker
            row['Qty'] = bagCalc.getStickerCount(customerOrders, this.rackConfigs);
            row['Barcodes'] = this.getQrCodes(customerName, ordersByCustomer[customerName]).join(', ');
            rows.push(row);
          })
        })
      }
      console.log('rows', rows);
      return rows;
    },
    distributionCities() {
      const cities = new Set();
      this.ordersUnfiltered.forEach(o => o.email && cities.add(o.distributionCity));
      return [...cities];
    }

  },
  methods: {
    ...mapActions([
      'fetchDiets',
      'reload'
    ]),
    getDeliveryInstruction,
    range,
    isOnfleetTask(orders) {
      return orders.some(o => o.isOnfleetTask);
    },
    getBagCount(orders) {
      return BagCalc.getNumberOfBags(orders);
    },
    setDates() {
      const dates = this.dates;
      console.log('set dates', dates.length, dates);
      if (this.isAdmin) {
        // this.dates = dates;
      } else {
        if (dates.length === 1) {
          this.dates = getProductionDays(dates[0]);
        }
      }
      console.log('set dates', this.dates);
    },
    getDatesInRange() {
      const dates = [];
      if (!this.dateFrom || !this.dateTo) {
        return dates;
      }
      const from = moment(this.dateFrom);
      const to = moment(this.dateTo);

      const diff = moment.duration(to.diff(from));
      if (diff.asDays() >= 0 && diff.asDays() <= 31) {
        // console.log('adding days ', {from, to})
        while (from.isSameOrBefore(to)) {
          const dateString = getDateString(from);
          // console.log('added date', dateString);
          dates.push(dateString);
          from.add(1, 'day');
        }
      } else {
        window.alert(diff.asDays() + ' days is above the limit of 7');
      }
      // console.log('getDatesInRange', dates);
      return dates;
    },
    /** reload the page's data even though the dates and shipping group have not changed */
    async refetchData() {
      // fetchData skips a load for the dates it already has, which made every refetch a no-op
      this.currentData = null;
      return this.fetchData();
    },
    async fetchData() {
      this.error = this.post = null
      const {dates, shippingGroup} = this;
      if (!dates || dates.length === 0) {
        console.log('no dates, no fetch');
        this.loading = false;
        return;
      }
      if (!shippingGroup) {
        console.log('no shippingGroup, no fetch');
        this.loading = false;
        return;
      }

      if (this.currentData && diff(this.currentData, {dates, shippingGroup}).length === 0) {
        console.log('already loaded', this.currentData);
        return
      } else {
        console.log('fetching prep', {dates, shippingGroup});
        this.currentData = {dates, shippingGroup};
      }

      this.loading = true;
      const params = {
        from: this.dateFrom,
        to: this.dateTo,
        shippingGroups: [this.shippingGroup],
        include_container_size: true
        // properties: ['quantity', 'allergyDetected', 'allergies', 'meal_id', 'stream', 'tod', 'meal_size'],
      };
      console.log('fetching ', params);
      this.ordersFetched = await api
        .get('v2/order/search', {
          params
        })
        .then(({data}) => data);

      const uids = [...new Set(this.ordersFetched.map(o => o.uid))];
      this.customersRaw = await api
        .post('v2/customers', {
          uids,
        })
        .then(({data}) => Object.fromEntries(data.map(u => [u.uid, u])));

      if (this.filterDistributionCity) {
        if (!this.distributionCities.includes(this.filterDistributionCity)) {
          this.filterDistributionCity = undefined;
        }
      }
      return Promise.all([
        this.fetchDriverTasks(),
      ])
        .catch((e) => {
          console.error('something went wrong', e);
        })
        .finally(() => {
          this.loading = false;
          // an export or check started earlier (maybe from a browser that went away) is still running
          this.resumeRunningJob();
        });
    },
    closeDatePicker() {
      this.showDatePicker = false;
      this.$nextTick(() => this.fetchData());
    },
    dateFormatted,
    groupOrdersBy(orders, prop = 'name') {
      if (prop === 'address') {
        return groupOrdersByDelivery(orders);
      }

      const result = {};
      const keyMap = {};
      orders.forEach(o => {
        let key;
        if (prop === 'name') {
          let name = o.name.trim();
          key = this.showUid ? (name + '-' + o.uid) : name;
        }
        if (prop === 'uid') {
          key = o.uid;
        }

        if (key) {
          const keyUpper = key.toUpperCase();
          keyMap[keyUpper] = keyMap[keyUpper] || key;
          result[keyUpper] = result[keyUpper] || [];
          result[keyUpper].push(o);
        }
      });
      return sortOrderGroups(result, keyMap);
    },
    isFirstOrder(customerOrder) {
      return customerOrder && customerOrder.isFirstOrder;
    },
    async resetOnfleetStatus() {
      if (confirm('this will erase the export status in orders allowing you to do a clean re-export.  if you have not deleted all tasks in onfleet you will end up with duplicates')) {
        console.log('reset all');
        this.exporting = true;
        const exportStatus = {errors: []}
        this.exportStatus = exportStatus;
        const result = await api.post('/v2/onfleet/reset-status', {orders: this.orders})
          .catch(e => console.error('failed', e));
        console.log('reset result', result.data);
        window.alert(`reset finished!

${JSON.stringify(result.data)}

click ok to reload the page.
`);
        await this.refetchData();
      }
      this.exporting = false;
    },
    /** opens the check dialog, for one stop or - with no orders - the whole shipment */
    async openOnfleetCheck(orders) {
      if (!orders && await this.watchRunningJob()) {
        return;
      }
      if (!orders) {
        const choice = await this.confirmFilters('checked');
        if (!choice) {
          return;
        }
        this.checkIgnoresFilters = choice === 'everything';
      }
      this.checkOrders = orders || null;
      this.checkFinished = false;
      this.checkCancelled = false;
      this.checkProgress = {ordersChecked: 0, ordersToCheck: 0, stopsChecked: 0, stopsToCheck: 0, messages: []};
      this.showCheckDialog = true;
    },
    /** while a check is running this stops it, otherwise it closes the dialog */
    async closeOnfleetCheck() {
      if (this.checking) {
        if (this.pageJob) {
          await this.stopPageJob();
        }
        this.checkCancelled = true;
        return;
      }
      this.showCheckDialog = false;
      if (this.checkFinished) {
        this.checkFinished = false;
        // the check removes references to tasks that are gone from onfleet
        await this.refetchData();
      }
    },
    /**
     * runs the check the dialog asked for: the export status check, a slack
     * message per stop, or one concise slack report for the whole shipment.
     * none of them create or change anything in onfleet.
     */
    async runOnfleetCheck(mode) {
      if (!this.checkOrders) {
        return this.startCheckJob(mode);
      }
      this.checkMode = mode;
      this.resetSkipCounts();
      const orders = this.checkOrders || this.checkPageOrders;
      // the api keeps one onfleet status per delivery (customer + day), so check one delivery per request
      const deliveries = mode === 'status' ? Object.entries(this.groupOrdersBy(orders, 'uid'))
        .flatMap(([uid, uidOrders]) => Object.entries(groupByKey(uidOrders, o => o.ship_date))
          .map(([shipDate, dayOrders]) => [`${uid} ${shipDate}`, dayOrders])) : [];
      const stops = mode === 'status' ? [] : Object.entries(this.groupOrdersBy(orders, 'address'));

      this.checking = true;
      this.checkCancelled = false;
      this.checkProgress = {
        ordersChecked: 0,
        ordersToCheck: deliveries.length,
        stopsChecked: 0,
        stopsToCheck: stops.length,
        messages: []
      };
      const progress = this.checkProgress;

      const count = {};
      // one line per delivery whose task no longer lists it (see the api's checkTaskListsOrder)
      const problems = [];
      for (const [delivery, deliveryOrders] of deliveries) {
        if (this.checkCancelled) break;
        const result = await api
          .post('/v2/onfleet/check-status', {orders: deliveryOrders})
          .catch(e => console.error('failed', e));
        const data = result && result.data;
        console.log('check result ' + delivery, data);
        const deliveryCount = (data && data.count) || {};
        for (const k of Object.keys(deliveryCount)) {
          count[k] = count[k] || 0;
          count[k] += deliveryCount[k];
        }
        problems.push(...((data && data.problems) || []));
        progress.ordersChecked++;
      }

      let failedStops = 0;
      let postedStops = 0;
      let problemStops = 0;
      let shipDate;
      const reportRows = [];
      for (const [address, stopOrders] of stops) {
        if (this.checkCancelled) break;
        try {
          const payload = await this.buildOnfleetPayload(stopOrders);
          if (payload) {
            console.log('checking in onfleet', address);
            shipDate = shipDate || payload.shipDate;
            const customers = [...new Set(stopOrders.map(o => o.uid))]
              .map(uid => ({uid, name: this.getCustomerName(uid)}));
            const result = await api.post('v2/onfleet/check-export/', {
              ...payload,
              customers,
              onlyProblems: mode === 'comparison' && this.checkOnlyProblems,
              collect: mode === 'concise'
            });
            const data = (result && result.data) || {};
            if (data.posted) postedStops++;
            if (data.problems) problemStops++;
            if (data.rows) reportRows.push(...data.rows);
          }
        } catch (e) {
          console.error('check failed', address, e);
          failedStops++;
        }
        progress.stopsChecked++;
      }

      let reportedCustomers = 0;
      if (mode === 'concise' && reportRows.length) {
        const result = await api
          .post('v2/onfleet/check-report/', {shipDate, rows: reportRows, stops: progress.stopsChecked})
          .catch(e => console.error('report failed', e));
        const data = (result && result.data) || {};
        reportedCustomers = data.problems || 0;
      }

      progress.messages = [
        this.checkCancelled && 'stopped before finishing.',
        // the api counts deliveries (one customer on one day), not meals
        count.checked > 0 && `${count.checked} deliveries have a task in onfleet.`,
        count.fixed > 0 && `${count.fixed} deliveries had a task that was deleted in onfleet, so they are now marked as not exported.`,
        count.driverFixed > 0 && `${count.driverFixed} deliveries had a different driver than onfleet, so the driver was updated.`,
        count.notLinked > 0 && `${count.notLinked} deliveries point at an onfleet task that no longer lists them, so the driver was left alone:`,
        ...problems,
        count.skipped > 0 && `${count.skipped} deliveries were not exported to onfleet (or the export failed), so there was nothing to check.`,
        count.deleted > 0 && `${count.deleted} deliveries had their onfleet status deleted.`,
        mode === 'comparison' && progress.stopsChecked > 0 && `${progress.stopsChecked} stops were compared with onfleet:`
        + ` ${problemStops} had a warning or an error, ${postedStops} posted to slack.`,
        mode === 'concise' && progress.stopsChecked > 0 && `${progress.stopsChecked} stops (${reportRows.length} customers)`
        + ` were compared with onfleet, ${reportedCustomers} in the slack report.`,
        failedStops > 0 && `${failedStops} stops could not be checked - see the browser console.`,
        mode === 'status' && !count.fixed && !count.deleted && !count.driverFixed && 'no changes were made.'
      ].filter(m => !!m);

      this.checking = false;
      this.checkCancelled = false;
      this.checkFinished = true;
    },
    /**
     * the Combine button on a half exported card (some customers already on a task): the
     * whole address is sent, and the api adds the others to that task. not proven against
     * the live onfleet yet, so it asks first
     */
    combineStop(stop) {
      if (!confirm(`Some customers at ${stop.address} are already on Onfleet task`
        + ` ${stop.tasks.map(t => t.shortId).join(', ')}.\n\n`
        + `Add ${stop.notExportedNames.join(', ')} to that task (it becomes "${stop.expectedName}")?`)) {
        return;
      }
      this.exportToOnfleet2({address: stop.address, orders: stop.orders});
    },
    /** "Export to Onfleet": check the filters, then open the dialog to start the export */
    async startExportAll() {
      if (this.exporting || this.loading) {
        return;
      }
      if (await this.watchRunningJob()) {
        return;
      }
      const choice = await this.confirmFilters('exported');
      if (!choice) {
        return;
      }
      const firstDate = this.dates.sort()[0];
      if (moment().add(1, 'day').isSameOrAfter(firstDate)) {
        if (!confirm('WARNING: you are running an export on tasks that should already have been delivered.  Bad things will happen!\n\n\nARE YOU SURE?!!?..')) {
          return;
        }
      }
      // decide what to export from fresh data: orders added since the page loaded, and
      // statuses changed by someone else, would otherwise be missed or wrongly skipped
      try {
        await this.refetchData();
      } catch (e) {
        console.error('refetch failed', e);
        alert('Could not reload the orders before exporting, so nothing was exported: ' + e.message);
        return;
      }
      const orders = choice === 'everything' ? this.ordersWithoutFilters : this.orders;
      this.exportSetup = {orders, deliveries: Object.keys(this.groupOrdersBy(orders, 'address')).length};
      // off by default: adding to a task an earlier export made is not proven against the live onfleet yet
      this.exportCombine = false;
      this.showExportDialog = true;
    },
    /** Start export: hand every address on the page to an export job on the api */
    async runExportAll() {
      const {orders} = this.exportSetup;
      const combine = this.exportCombine;
      this.exportSetup = null;
      this.exporting = true;
      this.startExportStatus(0);
      this.resetSkipCounts();
      try {
        const items = await this.buildExportItems(orders, combine);
        const {data} = await api.post('v2/onfleet/jobs', {kind: 'export', key: this.pageJobKey, items});
        // a job already running for this page is handed back instead of a new one
        this.watchJob(data.job);
      } catch (e) {
        console.error('export failed to start', e);
        alert('The export could not be started: ' + e.message);
        this.exporting = false;
      }
    },
    /**
     * one export job item per address. combine: an address where some customers are
     * already on a task is sent whole, and the api adds the rest to that task; without
     * it those addresses are left alone. an address on more than one task is only reported.
     */
    async buildExportItems(pageOrders, combine) {
      const items = [];
      for (const [address, orders] of Object.entries(this.groupOrdersBy(pageOrders, 'address'))) {
        const customerOrders = orders.filter(o => !o.isOnfleetTask);
        const taskIds = [...new Set(customerOrders.map(o => this.getOnfleetTaskId(o)).filter(id => !!id))];
        const someNotOnATask = customerOrders.some(o => !this.getOnfleetTaskId(o));
        if (taskIds.length > 1) {
          items.push({label: address, outcome: 'warnings', message: `already on ${taskIds.length} Onfleet tasks`
              + ` (${taskIds.join(', ')}) - not changed, combine them in Onfleet`});
        } else if (customerOrders.length && !someNotOnATask) {
          items.push({label: address, outcome: 'alreadyExported'});
        } else if (taskIds.length === 1 && !combine) {
          items.push({label: address, outcome: 'notCombined'});
        } else {
          const payload = !this.isOnfleetTask(orders) && await this.buildOnfleetPayload(orders);
          items.push(payload
            ? {label: address, request: this.stopRequest(payload, {collect: true})}
            : {label: address, outcome: 'skipped'});
        }
      }
      return items;
    },
    /** whole-page check: one job item per delivery (status) or per stop (the slack comparisons) */
    async startCheckJob(mode) {
      this.checkMode = mode;
      this.resetSkipCounts();
      this.checking = true;
      this.checkProgress = {ordersChecked: 0, ordersToCheck: 0, stopsChecked: 0, stopsToCheck: 0, messages: []};
      try {
        const orders = this.checkPageOrders;
        const items = [];
        if (mode === 'status') {
          // the api keeps one onfleet status per delivery (customer + day), so one item per delivery
          for (const [uid, uidOrders] of Object.entries(this.groupOrdersBy(orders, 'uid'))) {
            for (const [shipDate, dayOrders] of Object.entries(groupByKey(uidOrders, o => o.ship_date))) {
              items.push({label: `${this.getCustomerFullName(uid)} ${shipDate}`, request: {orders: this.slimOrders(dayOrders)}});
            }
          }
        } else {
          for (const [address, stopOrders] of Object.entries(this.groupOrdersBy(orders, 'address'))) {
            const payload = await this.buildOnfleetPayload(stopOrders);
            items.push(payload
              ? {label: address, request: this.stopRequest(payload, {
                  onlyProblems: mode === 'comparison' && this.checkOnlyProblems,
                  collect: mode === 'concise'
                })}
              : {label: address, outcome: 'skipped'});
          }
        }
        const {data} = await api.post('v2/onfleet/jobs', {kind: `check-${mode}`, key: this.pageJobKey, items});
        this.watchJob(data.job);
      } catch (e) {
        console.error('check failed to start', e);
        alert('The check could not be started: ' + e.message);
        this.checking = false;
      }
    },
    /** the api only reads uid, date and ship_date from the orders, so send just those */
    slimOrders(orders) {
      return orders.map(({uid, date, ship_date}) => ({uid, date, ship_date}));
    },
    /** the body /create and /check-export (and the jobs) take for one stop */
    stopRequest(payload, options = {}) {
      const customers = [...new Set(payload.orders.map(o => o.uid))]
        .map(uid => ({uid, name: this.getCustomerName(uid)}));
      return {...payload, orders: this.slimOrders(payload.orders), customers, ...options};
    },
    async fetchRunningJob() {
      const {data} = await api.get('v2/onfleet/jobs/running', {params: {key: this.pageJobKey}});
      return data.job;
    },
    /** if a job runs for this page, show it (instead of starting another); true when there is one */
    async watchRunningJob() {
      const job = await this.fetchRunningJob().catch(e => {
        console.error('could not ask for a running job', e);
        return null;
      });
      if (job) {
        this.watchJob(job);
      }
      return !!job;
    },
    /** on opening the page: a job left running (e.g. by a closed browser) gets its dialog back */
    resumeRunningJob() {
      if (!this.jobPoll && this.dates && this.dates.length && this.shippingGroup) {
        this.watchRunningJob();
      }
    },
    /** open the job's progress dialog and follow the job (polling the api) until it ends */
    watchJob(job) {
      this.stopWatchingJob();
      this.applyJob(job);
      if (job.kind === 'export') {
        this.showExportDialog = true;
      } else {
        this.showCheckDialog = true;
      }
      if (job.status !== 'running') {
        this.jobEnded(job);
        return;
      }
      this.jobPoll = setInterval(async () => {
        let latest;
        try {
          ({data: {job: latest}} = await api.get(`v2/onfleet/jobs/${job.id}`));
        } catch (e) {
          if (e.response && e.response.status === 404) {
            // the api forgot it (restarted, or finished a while ago): nothing more to show
            this.stopWatchingJob();
            this.exporting = false;
            this.checking = false;
          }
          return;
        }
        this.applyJob(latest);
        if (latest.status !== 'running') {
          this.stopWatchingJob();
          this.jobEnded(latest);
        }
      }, 1000);
    },
    stopWatchingJob() {
      clearInterval(this.jobPoll);
      this.jobPoll = null;
    },
    /** close the dialog but leave the job running on the api */
    hideJobDialog() {
      this.stopWatchingJob();
      this.showExportDialog = false;
      this.showCheckDialog = false;
      this.exporting = false;
      this.checking = false;
    },
    async stopPageJob() {
      if (!this.pageJob) {
        return;
      }
      const {data} = await api.post(`v2/onfleet/jobs/${this.pageJob.id}/stop`);
      this.applyJob(data.job);
    },
    applyJob(job) {
      this.pageJob = job;
      const running = job.status === 'running';
      if (job.kind === 'export') {
        const counts = job.counts || {};
        this.exporting = running;
        this.exportStatus = {
          tasksToComplete: job.total,
          tasksCompleted: job.done,
          exported: counts.exported || 0,
          alreadyExported: counts.alreadyExported || 0,
          notCombined: counts.notCombined || 0,
          skipped: counts.skipped || 0,
          warnings: counts.warnings || 0,
          failed: counts.failed || 0,
          warningList: job.warnings.map(w => ({address: w.label, message: w.message})),
          errors: job.errors.map(e => ({errorMessage: e.message, onfleetPayload: e.payload || {address: e.label}})),
          current: job.current
        };
      } else {
        const mode = job.kind.replace('check-', '');
        const byDelivery = mode === 'status';
        this.checkMode = mode;
        this.checking = running;
        this.checkFinished = !running;
        this.checkCancelled = running && job.stopRequested;
        this.checkProgress = {
          ordersChecked: byDelivery ? job.done : 0,
          ordersToCheck: byDelivery ? job.total : 0,
          stopsChecked: byDelivery ? 0 : job.done,
          stopsToCheck: byDelivery ? 0 : job.total,
          messages: [...job.messages, ...job.errors.map(e => `${e.label}: ${e.message}`)]
        };
      }
    },
    async jobEnded(job) {
      if (job.kind === 'export') {
        // the cards show the tasks the export made (a check refetches when its dialog closes)
        await this.refetchData();
      }
    },
    addExportWarning(address, message) {
      this.exportStatus.warningList.push({address, message});
      this.exportStatus.warnings++;
    },
    /**
     * before a whole-page export or check: with filters on, part of the page is hidden and
     * would be left out - and an address the filters cut in half becomes separate onfleet
     * stops. so run over everything unless the user says the filter is intentional.
     * returns 'everything', 'filtered', or null to stop.
     */
    resetFilters() {
      this.driverSelected = null;
      this.onlyErrors = false;
      this.notExported = null;
      this.search = '';
      this.filterLocation = null;
      this.filterDistributionCity = null;
      this.etaStatusFilter = null;
      this.taskFilter = null;
      this.sharedOnly = false;
    },
    confirmFilters(verb) {
      if (!this.activeFilters.length) {
        return Promise.resolve('everything');
      }
      return new Promise(resolve => {
        this.filterPrompt = {show: true, verb, resolve};
      });
    },
    /** closes the filter prompt with the choice its button made */
    answerFilterPrompt(choice) {
      const {resolve} = this.filterPrompt;
      this.filterPrompt = {show: false, verb: '', resolve: null};
      if (resolve) {
        resolve(choice);
      }
    },
    /** a toast that stays up until it is dismissed; one line per key, updated in place */
    setNotice(key, message) {
      this.$set(this.notices, key, message);
      this.showNotices = true;
    },
    clearNotices() {
      this.notices = {};
      this.showNotices = false;
    },
    startExportReport() {
      this.exportReport = {rows: [], stops: 0, shipDate: null};
      this.resetSkipCounts();
    },
    /** the toast counts what this run skipped, so start each run from zero */
    resetSkipCounts() {
      this.donationsRemoved = 0;
      this.pickupsSkipped = 0;
      this.clearNotices();
    },
    /** one slack message for the whole export, listing every stop it sent */
    async postExportReport() {
      const report = this.exportReport;
      if (!report || !report.rows.length) {
        return;
      }
      await api.post('v2/onfleet/check-report/', {
        shipDate: report.shipDate,
        rows: report.rows,
        stops: report.stops,
        mode: 'export'
      }).catch(e => console.error('export report failed', e));
    },
    async exportToOnfleet2({orders, exportAll = false}) {
      if (this.isOnfleetTask(orders)) {
        console.log('onfleet task, do not export', orders);
        return;
      }
      const exportOne = !exportAll;

      const payload = await this.buildOnfleetPayload(orders);
      if (!payload) {
        return;
      }

      if (exportOne) {
        this.exporting = true;
        this.showExportDialog = true;
        this.startExportStatus(1);
        this.startExportReport();
      }

      console.log('onfleet create', payload)
      const result = await api.post('v2/onfleet/create/', this.stopRequest(payload, {collect: true}))
        .catch(e => console.error('fail', e))
      // no response at all (network or server error) counts as a failure
      let outcome = 'failed';

      if (result) {
        const {data} = result;
        const report = this.exportReport;
        if (report) {
          report.stops++;
          report.shipDate = report.shipDate || payload.shipDate;
          if (data && data.rows) {
            report.rows.push(...data.rows);
          }
        }
        // a failure comes back as {error} when the api gave up early, or as rows in the
        // 'failed' group when onfleet refused the task
        const failedRow = data && data.rows && data.rows.find(r => r.group === 'failed');
        const error = (data && data.error) || (failedRow && failedRow.note);
        if (data && data.warning) {
          // e.g. the address turned out to be on two onfleet tasks: the api changed nothing
          this.exportStatus.warningList.push({address: payload.address, message: data.warning});
          outcome = 'warnings';
        } else if (error) {
          console.error('recoding error', error)
          const onfleetPayload = {...payload};
          delete onfleetPayload.orders;
          const uids = [...new Set(payload.orders.map(o => o.uid))];
          this.exportStatus.errors.push({uids, errorMessage: error, onfleetPayload});
          outcome = 'failed';
        } else {
          outcome = 'exported';
        }
        console.log('result', data);
      }

      if (exportOne) {
        this.countExportOutcome(outcome);
        this.exportStatus.tasksCompleted = 1;
        await this.postExportReport();
        await this.refetchData();
        this.exporting = false;
      }
      return outcome;
    },
    /**
     * a fresh progress record for the export dialog. every field is set before vue sees
     * the object: fields added later are not reactive, which left the dialog stuck at 0
     */
    startExportStatus(tasksToComplete) {
      this.exportStatus = {
        errors: [],
        tasksToComplete,
        tasksCompleted: 0,
        exported: 0,
        alreadyExported: 0,
        skipped: 0,
        failed: 0,
        warnings: 0,
        warningList: [],
        notCombined: 0,
        current: null
      };
    },
    /** 'exported' | 'failed' | 'skipped' | 'warnings' from exportToOnfleet2 */
    countExportOutcome(outcome) {
      this.exportStatus[outcome || 'skipped']++;
    },
    /**
     * the payload the export sends for one stop.  the check uses it as well, so
     * what we compare against onfleet is exactly what we would send.
     * returns nothing when the stop has nothing left to deliver, or when the
     * mixed ship date was not confirmed.
     */
    async buildOnfleetPayload(orders) {
      const ordersWithoutDonate = orders.filter(o => !o.donate);
      if (ordersWithoutDonate.length !== orders.length) {
        this.donationsRemoved += orders.length - ordersWithoutDonate.length;
        this.setNotice('donations', `${this.donationsRemoved} order(s) were removed from the export`
          + ' because they are marked as donations.');
        orders = ordersWithoutDonate;
        if (orders.length === 0) {
          return;
        }
      } else {
        console.log('no filtered', orders, ordersWithoutDonate);
      }

      // pickup orders are collected by the customer, so never exported to onfleet
      const ordersWithoutPickup = orders.filter(
        o => !isPickupAddress(getAddress(this.getCustomer2(o.uid), o.date), this.pickupLookup));
      if (ordersWithoutPickup.length !== orders.length) {
        this.pickupsSkipped += orders.length - ordersWithoutPickup.length;
        this.setNotice('pickup', `${this.pickupsSkipped} pickup order(s) were skipped`
          + ' - pickup orders are not exported to Onfleet.');
        orders = ordersWithoutPickup;
        if (orders.length === 0) {
          return;
        }
      }

      const shipDates = [...new Set(orders.map(o => o.ship_date))];
      if (shipDates.length > 1) {
        if (!confirm('orders contain more than 1 ship date: ' + shipDates.join(', ') + '\n'
          + 'Only the first date will be used! ' + shipDates[0])) {
          return;
        }
      }

      const earliest = this.getEarliestTime(orders);
      const latest = this.getLatestTime(orders);

      const uids = [...new Set(orders.map(o => o.uid))];
      const customers = uids.map(uid => this.getCustomer2(uid));

      const instructions = orders
        .map(o => getDeliveryInstruction(o.customer, o.date))
        .map(s => (s && s.trim) ? s.trim() : '');

      const addresses = orders
        .map(o => formatAddress(getAddress(this.getCustomer2(o.uid), o.date), {excludeUnit: true}));
      const units = orders
        .map(o => getAddress(this.getCustomer2(o.uid), o.date).unit);


      // the api sends a stop's saved coordinates to onfleet, which refuses them next to the
      // one-line address: they need the parsed address.  the api looks the address up by
      // the ship date, as the stop card does
      const stopAddress = getAddress(this.getCustomer2(orders[0].uid),
        moment(shipDates[0]).format(moment.HTML5_FMT.DATE));
      const address = coordinatesState(stopAddress) === 'current'
        ? onfleetParsedAddress(stopAddress)
        : addresses[0];
      const unit = units[0];

      const notes = [...new Set(instructions)].join(', \n');

      const names = [...new Set(customers.map(c => [c.first_name, c.last_name].join(' ')))];
      if (names.length > 1) {
        names.unshift(`${names.length} PEOPLE`);
      }
      const name = names.join(' |\n ');

      const ordersByCustomer = this.groupOrdersBy(orders, 'name');
      const qrCodes = [];
      for (const customerName of Object.keys(ordersByCustomer)) {
        qrCodes.push(...this.getQrCodes(customerName, ordersByCustomer[customerName]));
      }

      const mainAccountCustomer = customers.find(c => !c.mainAccount);
      let phone_number;
      if (mainAccountCustomer) {
        phone_number = mainAccountCustomer.phone_number;
      } else {
        phone_number = customers[0].phone_number;
      }


      const payload = {
        name,
        phone_number,
        quantity: 1,  // TODO: until bag count is accurate, force to 1
        // this is a bit more complex since customer meals aren't combined in bags
        // quantity : Object.values(this.groupOrdersBy(orders, 'name'))
        //   .reduce((sum, orders) => sum + this.getBagCount(orders), 0),
        shipDate: shipDates[0],
        earliest,
        latest,
        unit,
        address,
        notes,
        orders,
        qrCodes
      };

      if (this.overrideShipDate) {
        payload.forceShipDate = this.overrideShipDate;
      }

      return payload;
    },
    getCustomerLocations(customerOrders) {
      return [...new Set(customerOrders.map(o => getAddress(o.customer, o.date).location))];
    },
    getCustomerDeliveryDates(customerOrders) {
      return [...new Set(customerOrders.map(o => o.ship_date))];
    },
    getCustomerStreams(customerOrders) {
      return [...new Set(customerOrders.map(o => o.stream))];
    },
    /** the eta the customer sees: logistics status.estimatedArrivalTime, via order search */
    getEta(customerOrders) {
      const order = customerOrders.find(o => o.eta);
      return order && order.eta;
    },
    /** the actual delivery time, present once onfleet marks the task complete */
    getDeliveredAt(orders) {
      const order = orders.find(o => o.deliveryInfo && o.deliveryInfo.status && o.deliveryInfo.status.completionTime);
      return order && order.deliveryInfo.status.completionTime;
    },
    /** the time to judge the stop by: the actual delivery time once delivered, the eta before */
    getEtaOrDeliveredTime(orders) {
      return this.getDeliveredAt(orders) || this.getEta(orders);
    },
    /** true when the delivery window comes from the customer's address, not the defaults */
    hasCustomerSetTimes(customerOrders) {
      return customerOrders.some(o => {
        const address = o.address || {};
        return !!(address.earliest_time || address.latest_time);
      });
    },
    /**
     * minutes the eta (or actual delivery time) falls outside the delivery window:
     * positive = too late, negative = too early, null = no eta or inside the window
     */
    getEtaWindowMiss(orders) {
      const eta = this.getEtaOrDeliveredTime(orders);
      if (!eta) {
        return null;
      }
      const etaMoment = moment(eta);
      const etaMinutes = etaMoment.hours() * 60 + etaMoment.minutes();
      const earliest = parseTimeToMinutes(this.getEarliestTime(orders));
      const latest = parseTimeToMinutes(this.getLatestTime(orders));
      if (latest !== undefined && etaMinutes > latest) {
        return etaMinutes - latest;
      }
      if (earliest !== undefined && etaMinutes < earliest) {
        return etaMinutes - earliest;
      }
      return null;
    },
    /** eg "45 minutes too late", "1h 20m too early" */
    formatEtaWindowMiss(orders) {
      const miss = this.getEtaWindowMiss(orders);
      if (miss === null) {
        return '';
      }
      const minutes = Math.abs(miss);
      const hours = Math.floor(minutes / 60);
      const amount = hours ? `${hours}h ${minutes % 60}m` : `${minutes} minutes`;
      return `${amount} too ${miss > 0 ? 'late' : 'early'}`;
    },
    /**
     * 'ok' when the eta (or actual delivery time) is inside the delivery window,
     * 'warning' when it falls outside a default window,
     * 'error' when it falls outside a window the customer set,
     * null when there is no eta at all
     */
    getEtaStatus(orders) {
      if (!this.getEtaOrDeliveredTime(orders)) {
        return null;
      }
      if (this.getEtaWindowMiss(orders) === null) {
        return 'ok';
      }
      return this.hasCustomerSetTimes(orders) ? 'error' : 'warning';
    },
    /** the cards with at least one of these orders */
    stopsShownBy(orders) {
      const shown = new Set(orders);
      return this.deliveryStops.filter(stop => stop.orders.some(o => shown.has(o)));
    },
    getDeliveryStatus(customerOrders) {
      const logistics = customerOrders[0].logistics;
      if (!logistics) {
        return [];
      }
      /** FIXME: this isn't really great - shipments should not be saved inside each order record
       * we presume that the order line item record contains all the shipments of the week (not just
       * the day a particular line item is on */
      const ship_dates = [...new Set(customerOrders.map(o => o.ship_date))]
        .map(date => moment(date).format('ddd').toLowerCase());
      const filtered = logistics
        .filter(s => ship_dates.includes(s.day))
      if (filtered.length > 0) {
        return filtered;
      } else {
        return logistics;
      }
    },
    formatOnfleetPayload(payload) {
      return payload && payload.recipients
        ? payload.recipients.map(({name, notes, phone}) => `${name} (${phone}): ${notes}`).join(', ')
        : '';
    },
    /** a stop's address sent to the api is one line, or parsed when the stop has coordinates */
    addressLabel(address) {
      return address && typeof address === 'object' ? formatAddress(address, {excludeUnit: true}) : address;
    },
    getCustomerName(uid) {
      const c = this.getCustomer2(uid);
      return `${c.first_name} ${c.last_name}`
    },
    formatAddress,
    copyToClipboard(text) {
      try {
        navigator.clipboard.writeText(text);
      } catch (e) {
        alert('sorry, copy not supported on this device: ' + e.message)
      }
    },
    linkTask(orderRefs, onfleetTaskId, {force = false} = {}) {
      const payload = {
        orderRefs,
        onfleetShortCode: onfleetTaskId,
        force,
        ...this.linkDelivery
      };
      console.log('payload', payload);

      this.linking = true;
      api
        .post('v2/onfleet/link', payload)
        .then(r => {
          console.log('done', r);
          alert('task has been linked.  eta and driver assignment will be updated in ~5 mins');
          this.closeLinkDialog();
        })
        .catch(e => {
          console.error('fail', e);
          const response = e.response || {};
          const refused = response.status === 409 && response.data && response.data.problems;
          if (refused) {
            // the server found the task but it does not match this delivery; show why and offer to force it
            this.linkProblems = response.data.problems;
            this.linkTaskSummary = response.data.task;
            return;
          }
          const serverError = response.data && response.data.error;
          alert('link failed: ' + (serverError || e.message));
        })
        .finally(() => {
          this.linking = false;
        });
    },
    closeLinkDialog() {
      this.showLinkDialog = false;
      this.linkOrders = null;
      this.onfleetShortId = '';
      this.manualOnfleetTaskId = '';
      this.linkProblems = [];
      this.linkTaskSummary = null;
    },
    uniqueUids(orders) {
      return [...new Set(orders.map(o => o.uid))];
    },
    getEarliestTime: getEarliestDeliveryTime,
    getLatestTime: getLatestDeliveryTime,
    fetchDriverTasks() {
      return api.get('v2/onfleet/driver-tasks')
        .then(({data}) => {
          console.log('tasks', data);
          this.driverTasks = data;
        });
    },
    async downloadPDF(element) {
      const payload = [];
      this.generatingPDF = true;
      const drivers = this.driverSelected ? [this.driverSelected] : this.drivers;
      for (const driver of drivers) {
        const ordersByAddress = this.groupOrdersBy(this.orders.filter(o => o.driver === driver), 'address');
        let runs = 1;
        let rows = [];
        let driverPayload = {
          driver,
          rows,
          date: this.dateFrom,
        };
        for (const [address, orders] of Object.entries(ordersByAddress)) {
          driverPayload.eta = driverPayload.eta || moment(orders[0].eta).format('LT');
          try {
            // console.log('pdfing', address, orders);
            const ordersByName = this.groupOrdersBy(orders, 'name');

            const instructionMap = {};
            for (const [name, customerOrders] of Object.entries(ordersByName)) {
              const note = this.getDeliveryInstruction(customerOrders[0].customer, customerOrders[0].date)
              instructionMap[note] = name;
            }

            const names = [];
            for (const [name, customerOrders] of Object.entries(ordersByName)) {
              // console.log('customerOrders', customerOrders);
              const note = this.getDeliveryInstruction(customerOrders[0].customer, customerOrders[0].date)
              if (instructionMap[note] === name) {
                names.push({name, note})
              } else {
                names.push({name, note: ''})
              }
            }

            rows.push({address, names});

            if (names.length === 1 && names[0].name == "Fed (onfleet)") {
              // start a new run
              driverPayload.driver = `${driver} ${runs++}`;
              payload.push(driverPayload);
              console.log('starting a new sheet', driverPayload);
              rows = [];
              driverPayload = {
                driver: `${driver} ${runs}`,
                date: this.dateFrom,
                rows,
              }
            } else {
              // console.log('not new ' + names[0].name, names);
            }
          } catch (e) {
            console.error('failed', e);
          }
        }
        if (rows.length > 0) {
          payload.push(driverPayload);
        }
      }

      try {
        console.log('sending pdf request', payload);
        const response = await api.post('v2/render-pdf/driver', {drivers: payload}, {responseType: 'blob'});
        const blob = new Blob([response.data], {type: response.headers['content-type']});
        const link = document.createElement('a');
        if (element) {
          // this prevents the page from jumping when the click event happens
          // element.parentElement.appendChild(link);
        }

        link.href = URL.createObjectURL(blob);
        link.download = this.filename;
        link.click();
        URL.revokeObjectURL(link.href);
        this.generatingPDF = false;
        return true;
      } catch (e) {
        this.generatingPDF = false;
        console.error('failed', e);
        return false;
      }


    },
    /**
     * one card per address (one onfleet stop is how it should be), with what onfleet
     * actually has for it: the tasks its orders are on, onfleet tasks at the address not
     * linked to an order, and the customers not exported yet
     */
    groupOrdersByAddressWithTasks(orders) {
      return Object.entries(this.groupOrdersBy(orders, 'address')).map(([address, addressOrders]) => {
        const customerOrders = addressOrders.filter(o => !o.isOnfleetTask);
        const exported = customerOrders.filter(o => this.getOnfleetTaskId(o));
        const tasks = Object.values(groupByKey(exported, o => this.getOnfleetTaskId(o)))
          .map(taskOrders => this.getOnfleetTaskDetail(taskOrders));
        const notExportedUids = customerOrders.filter(o => !this.getOnfleetTaskId(o)).map(o => o.uid);
        const customers = Object.entries(this.groupOrdersBy(addressOrders, 'name'))
          .map(([name, nameOrders]) => ({
            name,
            uid: nameOrders[0].uid,
            driver: nameOrders[0].driver,
            isOnfleetTask: !!nameOrders[0].isOnfleetTask,
            qrCodes: this.getQrCodes(name, nameOrders),
            customerSetTimes: this.hasCustomerSetTimes(nameOrders),
            instruction: getDeliveryInstruction(nameOrders[0].customer, nameOrders[0].date),
            errors: this.getDeliveryStatus(nameOrders).filter(s => s.error).map(s => s.error)
          }));
        const deliveredAt = this.getDeliveredAt(addressOrders);
        const eta = this.getEta(addressOrders);
        // the export finds the address (and any coordinates saved on it) by the ship date
        const coordinateDate = customerOrders.length ? moment(customerOrders[0].ship_date).format(moment.HTML5_FMT.DATE) : null;
        const coordinateAddress = coordinateDate ? getAddress(customerOrders[0].customer, coordinateDate) : null;
        const coordinates = coordinateAddress && coordinateAddress.coordinates;
        const donations = customerOrders.filter(o => o.donate).length;
        return {
          address,
          driver: addressOrders[0].driver,
          customers,
          boxes: customers.filter(c => !c.isOnfleetTask).reduce((sum, c) => sum + c.qrCodes.length, 0),
          exported: this.isExported(addressOrders),
          onfleetTask: this.isOnfleetTask(addressOrders),
          onfleetIds: this.getOnfleetIds(addressOrders),
          hasUnexported: notExportedUids.length > 0,
          donate: donations === 0 ? null : (donations === customerOrders.length ? 'all' : 'some'),
          earliest: this.getEarliestTime(addressOrders),
          latest: this.getLatestTime(addressOrders),
          shipDate: dateFormatted(addressOrders[0].ship_date),
          etaText: deliveredAt
            ? `Delivered at ${dateFormatted(deliveredAt, {formatString: 'h:mma'})}`
            : (eta ? `ETA ${dateFormatted(eta, {formatString: 'h:mma'})}` : null),
          etaStatus: this.getEtaStatus(addressOrders),
          etaWindowMiss: this.formatEtaWindowMiss(addressOrders) || null,
          orders: addressOrders,
          tasks,
          unlinkedTaskIds: addressOrders.filter(o => o.isOnfleetTask).map(o => o.shortId),
          notExportedNames: [...new Set(notExportedUids.map(uid => this.getCustomerFullName(uid)))],
          stopsInOnfleet: customerOrders.length ? this.stopsPerAddress[this.stopAddressKey(customerOrders[0])] || 1 : 1,
          expectedName: this.onfleetTaskName(customerOrders.map(o => o.uid)),
          customerCount: new Set(customerOrders.map(o => o.uid)).size,
          uids: [...new Set(customerOrders.map(o => o.uid))],
          coordinateDate,
          // the address record the export uses; the map dialog looks it up with google
          deliveryAddress: coordinateAddress,
          // coordinates sent to onfleet instead of the address: 'current' while the address is
          // the one they were saved for, 'stale' once it was edited (no longer used)
          coordinates: coordinatesState(coordinateAddress)
            ? {...coordinates, state: coordinatesState(coordinateAddress)}
            : null,
          geocodeError: customers.some(c => c.errors.some(e => /geocod/i.test(String(e))))
        };
      });
    },
    /**
     * a stop card saved (or cleared) the coordinates on its customers' address records
     * (POST v2/onfleet/coordinates): put the same on the copies this page holds, so the
     * card shows them without reloading every delivery
     */
    setStopCoordinates({uids, date, coordinates}) {
      for (const uid of uids) {
        const record = getAddress(this.customersRaw[uid], date);
        if (!record) continue;
        if (coordinates) {
          this.$set(record, 'coordinates', coordinates);
        } else {
          this.$delete(record, 'coordinates');
        }
      }
    },
    getCustomerFullName(uid) {
      const customer = this.customersRaw[uid];
      return customer ? [customer.first_name, customer.last_name].join(' ').replace(/\s+/g, ' ').trim() : uid;
    },
    /** the task name the export gives a stop: "2 PEOPLE | name | name" */
    onfleetTaskName(uids) {
      const names = [...new Set(uids.map(uid => this.getCustomerFullName(uid)))];
      if (names.length > 1) {
        names.unshift(`${names.length} PEOPLE`);
      }
      return names.join(' | ');
    },
    /**
     * the onfleet task some orders are on, from the copy saved in firebase at export:
     * its name (older exports did not save it, so it is rebuilt from the customers the
     * task references) and a link to it in onfleet
     */
    getOnfleetTaskDetail(taskOrders) {
      const [order] = taskOrders;
      const data = order.deliveryInfo.data;
      const refs = (data.metadata || []).find(m => m.name === 'order_references');
      const uids = refs ? refs.value.map(ref => ref.split('/')[2]) : [order.uid];
      return {
        shortId: data.shortId,
        name: data.name || this.onfleetTaskName(uids),
        time: this.getDeliveredAt(taskOrders)
          ? `delivered ${dateFormatted(this.getDeliveredAt(taskOrders), {formatString: 'h:mma'})}`
          : (this.getEta(taskOrders) ? dateFormatted(this.getEta(taskOrders), {formatString: 'h:mma'}) : null),
        driver: order.driver,
        url: data.id && 'https://app.onfleet.com/dashboard#/table?taskEdit=false&open=task&taskId='
          + encodeURIComponent(data.id)
      };
    },
    getOnfleetTaskId(order) {
      return order.deliveryInfo && order.deliveryInfo.data && order.deliveryInfo.data.shortId;
    },
    stopAddressKey(order) {
      return `${order.ship_date} ${addressGroupKey(order.address).toUpperCase()}`;
    },
    getOnfleetIds(orders) {
      return [...new Set(orders.map(o => o.deliveryInfo && o.deliveryInfo.data && o.deliveryInfo.data.shortId))].filter(s => !!s);
    },
    isExported(orders) {
      return this.getOnfleetIds(orders).length > 0;
    },

    getQrCodes(customerName, orders) {
      return BagCalc.generateQRValues(customerName, {orders, rackConfigs: this.rackConfigs});
    },
    getCustomer2(uid) {
      if (!this.customersRaw[uid]) {
        console.log('could not find uid', uid, this.customersRaw)
      }
      return this.customersRaw[uid];
    },
    /**
     * read-only: download what this page holds (orders, their onfleet references,
     * the stops it groups them into, the filters) as json, to work out how deliveries
     * at one address ended up on different onfleet tasks. no api calls.
     */
    downloadDebugDump() {
      const deliveryInfo = o => {
        const info = o.deliveryInfo;
        if (!info) {
          return null;
        }
        const data = info.data || {};
        const refs = (data.metadata || []).find(m => m.name === 'order_references');
        return {
          status: info.status,
          shortId: data.shortId,
          state: data.state,
          worker: data.worker,
          timeCreated: data.timeCreated,
          orderReferences: refs ? refs.value : null,
          error: info.status === 'error' ? data : undefined
        };
      };
      const filteredOrders = new Set(this.orders);
      const orders = this.ordersUnfiltered.map(o => ({
        uid: o.uid,
        name: o.name,
        date: o.date,
        ship_date: o.ship_date,
        tod: o.tod,
        donate: o.donate,
        driver: o.driver,
        isOnfleetTask: o.isOnfleetTask,
        onfleetShortId: o.shortId,
        shown: filteredOrders.has(o),
        groupKey: addressGroupKey(o.address),
        address: o.address,
        // the export payload reads the customer's address, the grouping reads the order's
        customerAddress: o.customer ? getAddress(o.customer, o.date) : null,
        deliveryInfo: deliveryInfo(o)
      }));
      const stops = Object.entries(this.groupOrdersBy(this.ordersUnfiltered, 'address'))
        .map(([stop, stopOrders]) => ({
          stop,
          uids: [...new Set(stopOrders.map(o => o.uid))],
          shipDates: [...new Set(stopOrders.map(o => o.ship_date))],
          shortIds: this.getOnfleetIds(stopOrders),
          shownOrders: stopOrders.filter(o => filteredOrders.has(o)).length,
          orders: stopOrders.length
        }));
      const customers = Object.fromEntries(Object.entries(this.customersRaw || {})
        .map(([uid, c]) => [uid, c && {
          first_name: c.first_name,
          last_name: c.last_name,
          mainAccount: c.mainAccount,
          subAccounts: c.subAccounts ? Object.keys(c.subAccounts) : undefined,
          address: c.address
        }]));
      const dump = {
        generatedAt: new Date().toISOString(),
        page: window.location.href,
        dates: this.dates,
        shippingGroup: this.shippingGroup,
        filters: {
          driverSelected: this.driverSelected,
          onlyErrors: this.onlyErrors,
          notExported: this.notExported,
          search: this.search,
          filterLocation: this.filterLocation,
          filterDistributionCity: this.filterDistributionCity,
          etaStatusFilter: this.etaStatusFilter,
          taskFilter: this.taskFilter,
          sharedOnly: this.sharedOnly
        },
        stops,
        orders,
        customers,
        driverTasks: this.driverTasks
      };
      const blob = new Blob([JSON.stringify(dump, null, 2)], {type: 'application/json'});
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `shipments-debug-${this.shippingGroup}-${(this.dates || []).join('_')}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    exportAddressCSV() {
      csvExport(this.dataAsTable, this.exportFilename)
    },
  },
  props: {
    isAdmin: {type: Boolean, default: false, required: false},

  }
}
</script>
